import { ComponentProps, ComponentType, Ref } from 'react'

import { SizeOptions } from '../../types/types'
import { AstraChip } from '../AstraChip/AstraChip'
import { AstraTooltip } from '../AstraTooltip/AstraTooltip'
import { astraTwMerge } from '../Utilities/astraTwMerge'
import { LCHColor, lchSetOpacity, stringToLchColor } from '../Utilities/colorHelpers'

export type EntityChipVariant = 'primary' | 'secondary' | 'ghost' | 'muted'
export type EntityChipSize = Exclude<SizeOptions, 'large'>

export type AstraIconProps = ComponentProps<'svg'>

export type AstraEntityChipProps = {
    /**
     * Icon component to display in the colored squircle container. Required.
     */
    icon: ComponentType<AstraIconProps>
    /**
     * Label text to display.
     */
    label: string
    /**
     * Optional seed string for color generation (defaults to label).
     * Use this when the display label differs from the string used for color derivation.
     */
    colorSeed?: string
    /**
     * Visual variant of the chip
     */
    variant?: EntityChipVariant
    /**
     * Size of the chip
     */
    size?: EntityChipSize
    /**
     * LCH chroma value for color generation
     */
    chroma?: number
    /**
     * LCH luminance value for color generation
     */
    luminance?: number
    /**
     * Enable avalanche mixing for better hue distribution
     */
    avalanche?: boolean
    /**
     * data-testid for testing purposes
     */
    'data-test'?: string
    /**
     * Optional title attribute for tooltip
     */
    title?: string
    /**
     * Ref to the root element
     */
    ref?: Ref<HTMLDivElement>
    /**
     * If true, the tooltip will be hidden even if a title is provided.
     */
    hideTooltip?: boolean
}

const DEFAULT_CHROMA = 40
const DEFAULT_LUMINANCE = 56

function getChipColor(variant: EntityChipVariant, generatedColor: LCHColor): string {
    switch (variant) {
        case 'primary':
            return lchSetOpacity(generatedColor, 0.1)
        case 'secondary':
            return 'default'
        case 'ghost':
        case 'muted':
            return 'rgba(255, 255, 255, 0)'
    }
}

/**
 * AstraEntityChip displays a colored chip representing a domain entity (e.g. practitioner, OR room).
 * It generates a consistent color from the label text using LCH color space and renders the
 * provided icon in a colored squircle container.
 *
 * Uses AstraChip as its base rendering component.
 */
export const AstraEntityChip = ({
    ref,
    icon: Icon,
    label,
    colorSeed,
    variant = 'primary',
    size = 'small',
    chroma = DEFAULT_CHROMA,
    luminance = DEFAULT_LUMINANCE,
    avalanche = false,
    'data-test': dataTest,
    title,
    hideTooltip,
}: AstraEntityChipProps) => {
    const seed = colorSeed ?? label
    const generatedColor = stringToLchColor(seed, chroma, luminance, avalanche)
    const isMuted = variant === 'muted'
    const isSecondary = variant === 'secondary'

    const coloredIcon = (
        <Icon
            className={astraTwMerge(isSecondary ? 'text-default' : isMuted ? '!text-muted' : undefined)}
            style={{ color: !isSecondary && !isMuted ? generatedColor : undefined, opacity: 0.9 }}
        />
    )

    const chipColor = getChipColor(variant, generatedColor)

    return (
        <AstraTooltip tooltipContent={title} hidden={hideTooltip}>
            <AstraChip ref={ref} icon={coloredIcon} label={label} size={size} variant="filled" color={chipColor} data-test={dataTest} />
        </AstraTooltip>
    )
}
